import { useQuery } from '@urql/next'
import { graphql } from '../../generated/gql/gql'
import type { UserFilterInput } from '../../generated/gql/graphql'
import { SortEnumType, User_UserStatus } from '../../generated/gql/graphql'

const FILTER_USERS = graphql(`
    query FilterUsers($where: UserFilterInput!, $first: Int, $order:[ UserSortInput!]) {
        users(where: $where, first: $first, order: $order) {
            nodes {
                id
                email
                displayName
                firstName
                lastName
                imageUrl
                meetplaceId
                userStatus
                role
            } 
        }
    }
`)

const GET_USERS = graphql(`query GetUsers($order:[ UserSortInput!]) {
  users(order: $order) {
    nodes {
      id
      email
      displayName
      firstName
      lastName
      fullName
      imageUrl
      meetplaceId
      userStatus
      role
    }
  }
}`)

const useUsersFilter = (
    value: string,
    type = 'filter',
    excludedIds: string[] = [],
    excludeGuest = false,
    status?: string,
) => {
    const GUEST_ROLE_ID = excludeGuest
        ? 'e0306612-78a8-4636-8422-7eb1caa474f9'
        : ''

    const keyword = value || ''

    const [
        {
            data: filteredData,
            fetching: fetchingFilteredData,
            error: errorFilteredData,
        },
        getFilteredUsers,
    ] = useQuery({
        query: FILTER_USERS,
        variables: {
            where: {
                and: [
                    { id: { nin: excludedIds } },
                    {
                        or: [
                            { fullName: { contains: keyword } },
                            { email: { contains: keyword } },
                        ],
                    },
                    { role: { neq: GUEST_ROLE_ID } },
                    { userStatus: { neq: User_UserStatus.Deactivated } },
                    ...(status
                        ? [{ userStatus: { eq: status as User_UserStatus } }]
                        : []),
                ],
            },
            first: 15,
            order: { fullName: SortEnumType.Asc },
        },
        pause: true,
    })

    const [{ data, fetching, error }, getUsers] = useQuery({
        query: GET_USERS,
        variables: {
            order: { fullName: SortEnumType.Asc },
        },
        pause: true,
    })
    return type === 'filter'
        ? {
              getData: getFilteredUsers,
              data: filteredData,
              fetching: fetchingFilteredData,
              error: errorFilteredData,
          }
        : { getData: getUsers, data, fetching, error }
}

type UseFilterMeetplaceUsersArgs = {
    keyword: string
    pause: boolean
    where?: UserFilterInput
}

const useFilterMeetplaceUsers = ({
    keyword,
    pause,
    where,
}: UseFilterMeetplaceUsersArgs) => {
    const [{ data, fetching, error }] = useQuery({
        query: FILTER_USERS,
        variables: {
            where: {
                and: [
                    {
                        or: [
                            {
                                fullName: {
                                    contains: keyword,
                                },
                            },
                            { email: { contains: keyword } },
                        ],
                    },
                    { userStatus: { neq: User_UserStatus.Deactivated } },
                    ...(where ? [where] : []),
                ],
            },
            first: 15,
        },
        pause,
    })

    return {
        users: data?.users?.nodes ?? [],
        fetching,
        error,
    }
}
export { useUsersFilter, useFilterMeetplaceUsers }
